import { isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { AnalyticsItemBox } from './AnalyticsItemBox';
import { InfoTitle } from '../../common/InfoTooltip/InfoTitle';

import { InfoIcon } from '../../icons/InfoIcon';
import { AnalyticsMetricTrend } from './AnalyticsMetricTrend';
import { NEGATIVE, POSITIVE } from '../../../constants/mainConstants';

import styles from './AnalyticsMetrics.module.scss';

export function AnalyticsMetric({
  title,
  tooltip,
  value,
  prevValue,
  percentageChange,
  loading,
  isOppositeTrend = false,
}) {
  const hasPrevValue = !isNil(prevValue);

  const header = useMemo(() => {
    const isPositive = percentageChange > 0;

    let tooltipFragment = (
      <InfoTitle content={<div className={styles.tooltip}>{tooltip}</div>}>
        <div>
          <InfoIcon />
        </div>
      </InfoTitle>
    );

    if (!hasPrevValue) {
      tooltipFragment = <div className={styles.onlyTooltip}>{tooltipFragment}</div>;
    }

    const positiveTrend = isOppositeTrend ? NEGATIVE : POSITIVE;
    const negativeTrend = isOppositeTrend ? POSITIVE : NEGATIVE;
    const prevValueFragment = (
      <AnalyticsMetricTrend
        percentageChange={percentageChange ?? 0}
        percentageTrend={isPositive ? positiveTrend : negativeTrend}
        caretTrend={isPositive ? POSITIVE : NEGATIVE}
      />
    );

    return (
      <>
        {tooltip ? tooltipFragment : null}
        {hasPrevValue ? prevValueFragment : null}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPrevValue, percentageChange]);

  return (
    <AnalyticsItemBox title={title} className={styles.analyticsMetric} header={header} showLoader={loading}>
      {!loading && (
        <>
          <div className={styles.valueWrapper}>
            <span className={styles.value}>{value}</span>
          </div>
          <p className={styles.prevRow}>
            {hasPrevValue ? (
              <span>
                previous<span>*</span>: {prevValue}
              </span>
            ) : (
              'No Previous Data'
            )}
          </p>
        </>
      )}
    </AnalyticsItemBox>
  );
}

AnalyticsMetric.propTypes = {
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  prevValue: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  percentageChange: PropTypes.number,
  isOppositeTrend: PropTypes.bool,
  loading: PropTypes.bool,
};
